/**
 * Epic 278 Slice E — friendly names for list/queue surfaces.
 * Raw camera/device/zone/workflow IDs stay under Technical details only (§17.3 / §19).
 */

export type TechnicalIdEntry = {
  label: string;
  value: string;
};

/** True when a string looks like an internal/raw identifier, not a customer-facing name. */
export function looksLikeRawTechnicalId(value?: string | null): boolean {
  const text = String(value || '').trim();
  if (!text) return false;
  if (/^(workflow|finding|report|incident|case|camera|cam|device|edge|zone)[-_:/.][a-z0-9._:-]+$/i.test(text)) {
    return true;
  }
  if (/^cam[_-][a-z0-9]+$/i.test(text)) return true;
  if (/^demo\d+$/i.test(text)) return true;
  if (/^INC[-_:][A-Z0-9._:-]+$/i.test(text)) return true;
  // Short opaque device-style tokens (e.g. edge1) — not spaced friendly names.
  if (/^[a-z]+\d+$/i.test(text) && text.length <= 16) return true;
  // Compact hex / opaque tokens without spaces (e.g. a1bc71b70d77).
  if (/^[a-z0-9]{8,}$/i.test(text) && !/\s/.test(text) && /[0-9]/.test(text) && /[a-f]/i.test(text)) {
    return true;
  }
  return false;
}

/**
 * Prefer a friendly label; never surface a raw ID as the primary customer label.
 * When label equals id or looks raw, return null so callers can omit or move to Technical details.
 */
export function friendlyDisplayLabel(
  label?: string | null,
  id?: string | null,
): string | null {
  const friendly = String(label || '').trim();
  const rawId = String(id || '').trim();
  if (friendly && !looksLikeRawTechnicalId(friendly) && friendly.toLowerCase() !== rawId.toLowerCase()) {
    return friendly;
  }
  if (friendly && !rawId && !looksLikeRawTechnicalId(friendly)) {
    return friendly;
  }
  return null;
}

export function collectTechnicalIds(parts: {
  cameraId?: string | null;
  cameraLabel?: string | null;
  deviceId?: string | null;
  deviceLabel?: string | null;
  zoneId?: string | null;
  zoneLabel?: string | null;
  workflowId?: string | null;
}): TechnicalIdEntry[] {
  const entries: TechnicalIdEntry[] = [];
  const push = (label: string, value?: string | null, displayLabel?: string | null) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return;
    const shownFriendly = friendlyDisplayLabel(displayLabel, trimmed);
    // Include when value is raw, or when friendly name is absent so operators can still inspect.
    if (looksLikeRawTechnicalId(trimmed) || !shownFriendly) {
      entries.push({ label, value: trimmed });
    }
  };
  push('Camera ID', parts.cameraId, parts.cameraLabel);
  push('Device ID', parts.deviceId, parts.deviceLabel);
  push('Zone ID', parts.zoneId, parts.zoneLabel);
  const workflow = String(parts.workflowId || '').trim();
  if (workflow && looksLikeRawTechnicalId(workflow)) {
    entries.push({ label: 'Workflow ID', value: workflow });
  }
  return entries;
}
